import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CalendarDays, Clock, MapPin, Wrench, Home as HomeIcon, Hammer, X } from "lucide-react";
import { MYBOOK } from "@/constants/testIds";
import { PhotoUpload, PhotoGallery } from "@/components/PhotoUpload";

const STATUS_MAP = {
  pending: { label: "In attesa", cls: "bg-[#FFF4E5] text-[#7A5211] border-[#F5A623]" },
  approved: { label: "Confermata", cls: "bg-primary/10 text-primary border-primary/30" },
  rejected: { label: "Rifiutata", cls: "bg-destructive/10 text-destructive border-destructive/30" },
  completed: { label: "Completata", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  cancelled: { label: "Annullata", cls: "bg-foreground/5 text-foreground/60 border-border" },
};

const SVC_ICON = { sopralluogo: HomeIcon, showroom: Hammer, riparazione: Wrench };
const SVC_LABEL = { sopralluogo: "Sopralluogo in cantiere", showroom: "Appuntamento in show room", riparazione: "Intervento di riparazione" };

export default function MyBookings() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { nav("/login"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/bookings");
      setItems(data);
    } catch (e) { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Annullare questa prenotazione?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success("Prenotazione annullata");
      load();
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-10 md:py-14">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <div className="text-xs tracking-widest uppercase text-primary">Area riservata</div>
          <h1 className="font-display text-3xl md:text-5xl mt-2">Le mie prenotazioni</h1>
        </div>
        <Button onClick={() => nav("/prenota")} className="rounded-full self-start" data-testid="new-booking-btn">Nuova prenotazione</Button>
      </div>

      <div className="mt-8 space-y-4" data-testid={MYBOOK.list}>
        {loading && <div className="text-foreground/60">Caricamento…</div>}
        {!loading && items.length === 0 && (
          <div className="bg-white border border-border rounded-xl p-10 text-center">
            <div className="font-display text-2xl">Nessuna prenotazione</div>
            <div className="text-sm text-foreground/60 mt-2">Prenota il tuo primo appuntamento.</div>
            <Button onClick={() => nav("/prenota")} className="mt-5 rounded-full">Prenota ora</Button>
          </div>
        )}
        {items.map((b) => <MyBookingItem key={b.booking_id} b={b} onCancel={cancel} />)}
      </div>
    </div>
  );
}

function MyBookingItem({ b, onCancel }) {
  const Icon = SVC_ICON[b.service_type] || CalendarDays;
  const st = STATUS_MAP[b.status] || STATUS_MAP.pending;
  const [photos, setPhotos] = useState([]);
  const canAddPhotos = b.service_type === "riparazione" && ["pending","approved"].includes(b.status);
  const loadPhotos = () => {
    if (b.service_type !== "riparazione") return;
    api.get(`/bookings/${b.booking_id}/photos`).then(r => setPhotos(r.data)).catch(() => {});
  };
  useEffect(() => { loadPhotos(); /* eslint-disable-next-line */ }, [b.booking_id]);
  return (
    <div data-testid={MYBOOK.item(b.booking_id)} className="bg-white border border-border rounded-xl p-5 md:p-6 card-lift">
      <div className="flex flex-col md:flex-row md:items-start gap-4">
        <div className="h-11 w-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="font-display text-xl">{SVC_LABEL[b.service_type]}</div>
            <Badge className={`border ${st.cls}`} data-testid={MYBOOK.statusBadge(b.booking_id)}>{st.label}</Badge>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-foreground/70 mt-2">
            <div className="flex items-center gap-1.5"><CalendarDays className="h-4 w-4"/> {b.date}</div>
            <div className="flex items-center gap-1.5"><Clock className="h-4 w-4"/> {b.time_slot}</div>
            {b.address && <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4"/> {b.address}</div>}
          </div>
          {b.repair_type && <div className="text-sm mt-2"><span className="text-foreground/60">Riparazione:</span> {b.repair_type}</div>}
          {b.description && <div className="text-sm mt-2 text-foreground/70">"{b.description}"</div>}
          {b.admin_notes && <div className="text-sm mt-3 p-3 rounded-md bg-secondary"><b>Note dal team:</b> {b.admin_notes}</div>}
          {photos.length > 0 && <PhotoGallery bookingId={b.booking_id} photos={photos} />}
          {canAddPhotos && (
            <div className="mt-3">
              <PhotoUpload bookingId={b.booking_id} compact onUploaded={loadPhotos} />
            </div>
          )}
        </div>
        {["pending","approved"].includes(b.status) && (
          <Button variant="outline" size="sm" onClick={() => onCancel(b.booking_id)} className="rounded-full text-destructive border-destructive/30 hover:bg-destructive/5" data-testid={MYBOOK.cancelBtn(b.booking_id)}>
            <X className="h-4 w-4 mr-1"/> Annulla
          </Button>
        )}
      </div>
    </div>
  );
}
